/**
 * Audit Log Viewer — searchable, filterable list of all audit entries.
 *
 * RBAC: Admin full access; Contract Specialist and Controller read-only.
 *
 * Requirements: 13.3, 13.4, 13.5
 */

import { useState, useEffect, useCallback } from 'react';

interface AuditLogEntry {
  logId: number;
  timestamp: string;
  userId: string;
  operationType: string;
  entityType: string;
  entityId: string;
  description: string | null;
  previousValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  bulkOperationId: string | null;
}

interface AuditLogResponse {
  data: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function AuditLogViewer() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [operationType, setOperationType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '50');
      if (search) params.set('search', search);
      if (operationType) params.set('operationType', operationType);
      if (entityType) params.set('entityType', entityType);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await fetch(`/api/audit?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load audit log.');
      const data: AuditLogResponse = await res.json();
      setEntries(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, search, operationType, entityType, startDate, endDate]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchAuditLog();
  };

  return (
    <div >
      <h1 className="text-lg font-semibold text-text-primary mb-6">Audit Log</h1>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 flex-1 min-w-48"
          aria-label="Search audit log"
        />
        <select
          value={operationType}
          onChange={(e) => { setOperationType(e.target.value); setPage(1); }}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Filter by operation type"
        >
          <option value="">All Operations</option>
          <option value="Create">Create</option>
          <option value="Update">Update</option>
          <option value="Delete">Delete</option>
          <option value="BulkOperation">Bulk Operation</option>
          <option value="AccessDenied">Access Denied</option>
        </select>
        <select
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Filter by entity type"
        >
          <option value="">All Entities</option>
          <option value="Vendor">Vendor</option>
          <option value="Contract">Contract</option>
          <option value="Invoice">Invoice</option>
          <option value="ComplianceDocument">Compliance</option>
          <option value="UserRoleAssignment">User Roles</option>
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          aria-label="End date"
        />
        <button type="submit" className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90">
          Filter
        </button>
      </form>

      {error && <p className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5">{error}</p>}

      <div className="text-sm text-slate-500 mb-3">{total} entries found</div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-page border-b border-border">
              <th className="text-left p-2">Timestamp</th>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Operation</th>
              <th className="text-left p-2">Entity</th>
              <th className="text-left p-2">ID</th>
              <th className="text-left p-2">Description</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-4 text-center text-slate-400">Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-center text-slate-400">No entries found.</td></tr>
            ) : (
              entries.map((entry) => (
                <>
                  <tr
                    key={entry.logId}
                    className="border-b border-border hover:bg-page/50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === entry.logId ? null : entry.logId)}
                  >
                    <td className="p-2 whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                    <td className="p-2">{entry.userId}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        entry.operationType === 'Create' ? 'bg-green-100 text-green-700' :
                        entry.operationType === 'Update' ? 'bg-baker-accent/20 text-text-primary' :
                        entry.operationType === 'Delete' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {entry.operationType}
                      </span>
                    </td>
                    <td className="p-2">{entry.entityType}</td>
                    <td className="p-2 font-mono text-xs">{entry.entityId}</td>
                    <td className="p-2 max-w-xs truncate">{entry.description}</td>
                  </tr>
                  {expandedId === entry.logId && (
                    <tr key={`${entry.logId}-detail`} className="bg-page/50">
                      <td colSpan={6} className="p-4">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          {entry.previousValues && (
                            <div>
                              <div className="font-medium mb-1">Previous Values:</div>
                              <pre className="bg-white p-3 rounded-lg border border-border overflow-x-auto">
                                {JSON.stringify(entry.previousValues, null, 2)}
                              </pre>
                            </div>
                          )}
                          {entry.newValues && (
                            <div>
                              <div className="font-medium mb-1">New Values:</div>
                              <pre className="bg-white p-3 rounded-lg border border-border overflow-x-auto">
                                {JSON.stringify(entry.newValues, null, 2)}
                              </pre>
                            </div>
                          )}
                          {entry.bulkOperationId && (
                            <div className="col-span-2">
                              <span className="font-medium">Bulk Operation ID:</span>{' '}
                              <span className="font-mono">{entry.bulkOperationId}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
